<template>
  <v-card outlined class="mx-auto">
    <template>
      <v-card-subtitle class="font-weight-bold">
        <div class="d-flex flex-column flex-md-row justify-space-between align-md-center">
          <div class="d-flex flex-column align-start mb-2 mb-md-0">
            <div class="d-flex align-center">
              {{ $vuetify.lang.t("$vuetify.gpt_sessions.session_list_title") }}
              &nbsp;
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-on="on" v-bind="attrs"> mdi-help-circle-outline </v-icon>
                </template>
                <span>
                  {{ $vuetify.lang.t("$vuetify.gpt_sessions.session_list_title") }}
                </span>
              </v-tooltip>
            </div>

            <div class="d-flex align-center flex-wrap mt-2">
              <v-chip class="mr-2" small color="#A9A9A9" text-color="white" label>
                {{ $vuetify.lang.t("$vuetify.gpt_sessions.total_sessions") }}: {{ totalHits }}
              </v-chip>
              <v-chip small color="primary" text-color="white" label>
                {{ $vuetify.lang.t("$vuetify.gpt_sessions.active_count") }}: {{ activeCount }}
              </v-chip>
            </div>
          </div>

          <div class="d-flex flex-row align-center mt-2">
            <v-btn
              class="mr-1"
              small
              color="primary"
              @click="gptSessionFileGenerationModal = true"
              :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
            >
              <v-icon left small>mdi-download</v-icon>
              <span class="hidden-sm-and-down"> CSV </span>
            </v-btn>
            <v-btn
              class="ml-1"
              small
              color="primary"
              @click="gptSessionFileHistoryModal = true"
              :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
            >
              <v-icon left small>mdi-history</v-icon>
              <span class="hidden-sm-and-down">
                {{ $vuetify.lang.t("$vuetify.common.history") }}
              </span>
            </v-btn>
          </div>
        </div>
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-row class="d-flex flex-column flex-md-row py-4 mb-n6 px-6">
        <v-col width class="pa-0 pr-0 pr-md-3">
          <v-text-field
            v-model="sessionSearchText"
            :label="$vuetify.lang.t('$vuetify.gpt_sessions.search_txt')"
            outlined
            dense
            clearable
          ></v-text-field>
        </v-col>
        <v-col width class="pa-0 pr-0 pr-md-3">
          <v-text-field
            v-model="searchBySessionId"
            :label="$vuetify.lang.t('$vuetify.gpt_sessions.search_session_by_ID')"
            outlined
            dense
            clearable
          ></v-text-field>
        </v-col>
        <v-col width class="pa-0 pr-0 pr-md-3">
          <v-text-field
            v-model="searchByCode"
            :label="$vuetify.lang.t('$vuetify.gpt_sessions.search_session_by_code')"
            outlined
            dense
            clearable
          ></v-text-field>
        </v-col>
        <v-col class="pa-0 pr-0 pr-md-3 ml-md-3">
          <v-combobox
            :items="gptProfiles"
            item-text="name"
            item-value="id"
            :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_profile_name')"
            outlined
            dense
            clearable
            return-object
            :loading="isProfileListLoading"
            @blur="resetExpandedProfilesOnClose"
            @change="handleProfileChange"
            :menu-props="{ contentClass: 'custom-zindex' }"
          >
            <template v-slot:item="{ item, on, attrs }">
              <div style="width: 100%">
                <div
                  v-bind="attrs"
                  v-on="on"
                  class="d-flex align-center justify-space-between px-0 py-2"
                  style="width: 100%"
                >
                  <span style="font-size: 0.8125rem; font-weight: 500">{{ truncate(item.name) }}</span>
                  <v-btn icon small @click.stop="getSessionCountByProfile(item)">
                    <v-icon small>
                      {{ chevronIcon(item.id) }}
                    </v-icon>
                  </v-btn>
                </div>
                <v-expand-transition>
                  <div v-if="expandedItems[item.id]" class="px-1 pb-3 text--secondary">
                    <div v-if="loadingItems[item.id]" class="d-flex align-center">
                      <v-progress-circular indeterminate size="16" width="2" class="mr-2"></v-progress-circular>
                      <span class="text--disabled" style="font-size: 0.7rem">{{
                        $vuetify.lang.t("$vuetify.gpt_sessions.session_count_loading_state")
                      }}</span>
                    </div>
                    <div v-else>
                      <p class="ma-0 small-text">
                        {{ $vuetify.lang.t("$vuetify.gpt_sessions.session_count_all_sessions") }}
                        {{ item.total_sessions }}
                      </p>
                      <p class="ma-0 small-text">
                        {{ $vuetify.lang.t("$vuetify.gpt_sessions.session_count_active_sessions") }}
                        {{ item.total_active_sessions }}
                      </p>
                      <p class="ma-0 small-text">
                        {{ $vuetify.lang.t("$vuetify.gpt_sessions.session_count_finished_sessions") }}
                        {{ item.total_finished_sessions }}
                      </p>
                      <p class="ma-0 small-text">
                        {{ $vuetify.lang.t("$vuetify.gpt_sessions.session_count_blocked_sessions") }}
                        {{ item.total_blocked_sessions }}
                      </p>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </template>

            <template v-slot:append-item v-if="hasMoreProfiles">
              <v-list-item>
                <v-list-item-content>
                  <div class="d-flex">
                    <v-btn class="ma-0 pa-0" @click="getGPTProfiles()" color="primary" text>
                      {{ $vuetify.lang.t("$vuetify.common.load_more") }}
                    </v-btn>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-combobox>
        </v-col>
        <v-col class="pa-0 pr-0 pr-md-3 ml-md-3">
          <v-select
            v-model="archiveFilter"
            :items="archiveOptions"
            :label="$vuetify.lang.t('$vuetify.gpt_history.filter_archive')"
            outlined
            dense
            clearable
          ></v-select>
        </v-col>
      </v-row>
      <v-divider></v-divider>

      <!-- Session Table start -->
      <v-data-table
        :headers="sessionListHeaders"
        :items="sessionList"
        :loading="isLoading"
        :loading-text="$vuetify.lang.t('$vuetify.common.loading_text')"
        :server-items-length="totalHits"
        :items-per-page="itemsPerPage"
        :page="page"
        :multi-sort="true"
        show-expand
        item-key="session_id"
        :expanded.sync="expandedRows"
        @update:page="page = $event"
        @update:items-per-page="itemsPerPage = $event"
        :options.sync="gptSessionOptions"
        :footer-props="{ showFirstLastPage: true }"
      >
        <template v-slot:item.session_id="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                {{ item.session_id ? item.session_id.split("-")[0] : "" }}
              </span>
            </template>
            <span>{{ item.session_id || "N/A" }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.session_title="{ item }">
          <TruncateTooltip
            :text="item.session_title"
            :limit="30"
            :tooltip-max-width="350"
            :tooltip-only-when-truncated="true"
          />
        </template>
        <template v-slot:item.session_code="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                {{ item.session_code ? item.session_code : "N/A" }}
              </span>
            </template>
            <span>{{ item.session_code || "N/A" }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.user_name="{ item }">
          {{ item.user_name || "N/A" }}
        </template>
        <template #item.maira_profile_name="{ item }">
          <TruncateTooltip
            :text="item.maira_profile_name"
            :limit="30"
            :tooltip-max-width="350"
            :tooltip-only-when-truncated="true"
          />
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template v-slot:item.last_conversation_at="{ item }">
          {{ item.last_conversation_at ? formatDate(item.last_conversation_at) : "N/A" }}
        </template>
        <template v-slot:item.session_status="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="d-block text-truncate" style="max-width: 150px">
                {{ item.session_status ? item.session_status : "" }}
              </div>
            </template>
            <span>{{ item.session_status || "N/A" }}</span>
          </v-tooltip>
        </template>
        <template #item.session_ended_at="{ item }">
          {{ item.session_ended_at ? formatDate(item.session_ended_at) : "N/A" }}
        </template>
        <template v-slot:item.session_closure_reason="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="d-block text-truncate" style="max-width: 150px">
                {{ item.session_closure_reason ? item.session_closure_reason : "N/A" }}
              </div>
            </template>
            <span>{{ item.session_closure_reason || "N/A" }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.ip_address="{ item }">
          <div v-if="getIpAddresses(item).length > 0">
            <span>{{ getIpAddresses(item)[0] }}</span>
            <span
              v-if="getIpAddresses(item).length > 1"
              class="ml-2 clickable-more"
              @click.stop="toggleRowExpansion(item)"
            >
              +{{ getIpAddresses(item).length - 1 }} {{ $vuetify.lang.t("$vuetify.common.more") }}
            </span>
          </div>
          <div v-else>N/A</div>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <ButtonGroup
            :groupType="ACTION_BUTTON_GROUP"
            :editButton="false"
            :redirectButton="true"
            :addButton="false"
            :startButton="false"
            :stopButton="false"
            :deleteButton="false"
            :closeButton="true"
            :disabledCloseButton="!$hasProjectAccessFor([$pRole.ADMIN]) || item.session_status !== 'active'"
            :disabled-redirect-button="!$hasProjectAccessFor([$pRole.ADMIN, $pRole.USER])"
            @onDetailsClick="openSessionDetailsModal(item)"
            @onRedirectClick="redirectSessionConversationDetails(item)"
            @onCloseClick="
              sessionCloseDialog = true;
              sessionToBeBlockedOrClosed = item;
            "
            @onBlockClick="
              sessionToBeBlockedOrClosed = item;
              sessionBlockDialog = true;
            "
          />
        </template>
        <template v-slot:expanded-item="{ item }">
          <td :colspan="sessionListHeaders.length + 1">
            <v-row class="py-4">
              <v-col cols="12">
                <div class="d-flex align-center flex-wrap">
                  <div class="text-subtitle-2 font-weight-medium mr-3 mb-0">
                    {{ $vuetify.lang.t("$vuetify.common.ip_address") }}:
                  </div>

                  <div v-if="getIpAddresses(item).length > 0">
                    <span class="ip-list">
                      {{ getIpAddresses(item).join(", ") }}
                    </span>
                  </div>

                  <div v-else>N/A</div>
                </div>
              </v-col>
            </v-row>
          </td>
        </template>
      </v-data-table>
      <!-- Session Table end -->

      <!-- Session Modal start -->
      <template v-if="sessionDetailsModal">
        <v-dialog v-model="sessionDetailsModal" max-width="800px">
          <v-card>
            <v-card-title class="sesstion-details-card-title">
              <div>
                <span class="headline"
                  ><strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_id") }}:</strong>
                  {{ sessionDetails.session_id }}</span
                >
              </div>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="11">
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_title") }}:</strong>
                    {{ sessionDetails.session_title || "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_code") }}:</strong>
                    {{ sessionDetails.session_code || "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_public_url") }}:</strong>
                    {{ sessionDetails.session_url || "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_maira_profile") }}:</strong>
                    {{ sessionDetails.maira_profile_id || "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.profile_name") }}:</strong>
                    {{ sessionDetails.maira_profile_name || "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.is_session_public_flag") }}:</strong>
                    {{ sessionDetails.is_session_public }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_user_id") }}:</strong>
                    {{ sessionDetails.user_id }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_user_name") }}:</strong>
                    {{ sessionDetails.user_name || "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_member_id") }}:</strong>
                    {{ sessionDetails.member_id || "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_creation_date") }}:</strong>
                    {{ sessionDetails.created_at ? formatDate(sessionDetails.created_at) : "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.last_conversation_at") }}:</strong>
                    {{ sessionDetails.last_conversation_at ? formatDate(sessionDetails.last_conversation_at) : "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_status") }}:</strong>
                    {{ sessionDetails.session_status || "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_ended_at") }}:</strong>
                    {{ sessionDetails.session_ended_at ? formatDate(sessionDetails.session_ended_at) : "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.session_closure_reason") }}:</strong>
                    {{ sessionDetails.session_closure_reason || "N/A" }}
                  </div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.gpt_sessions.platform") }}:</strong>
                    {{ sessionDetails.platform || "N/A" }}
                  </div>
                  <div><strong>LINE UID:</strong> {{ sessionDetails.line_uid || "N/A" }}</div>
                  <div>
                    <strong>{{ $vuetify.lang.t("$vuetify.common.ip_address") }}:</strong>
                    {{ getIpAddresses(sessionDetails).join(", ") || "N/A" }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions class="d-flex justify-end">
              <v-btn text color="error" @click="closeSessionDetailsModal()">
                {{ $vuetify.lang.t("$vuetify.common.close_btn") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
      <!-- Session Modal end -->
      <template v-if="gptSessionFileGenerationModal">
        <FileGenerationModal
          :open="gptSessionFileGenerationModal"
          :closeModal="
            () => {
              gptSessionFileGenerationModal = false;
            }
          "
          :title="$vuetify.lang.t('$vuetify.logs_file_generation.session_logs_file_generation_title')"
          :downloadRequestTxt="$vuetify.lang.t('$vuetify.logs_file_generation.session_history_download_request_txt')"
          :fileGenerationInstructionTxt="
            $vuetify.lang.t('$vuetify.logs_file_generation.generate_session_history_instruction')
          "
          :requestFileGeneration="sessionLogDownloadRequest"
          :prefix="session_history_prefix"
          :history_type="HISTORY_TYPES.SESSION"
          :filters="{
            start_date,
            end_date,
            selectedProfileId,
            searchBySessionId,
            searchByCode,
            archiveFilter,
            search_text: sessionSearchText,
          }"
        />
      </template>
      <template v-if="gptSessionFileHistoryModal">
        <FileGeneratedHistoryModal
          :open="gptSessionFileHistoryModal"
          :closeModal="
            () => {
              gptSessionFileHistoryModal = false;
            }
          "
          :title="$vuetify.lang.t('$vuetify.logs_file_generation.session_logs_file_generated_history_title')"
          :prefix="session_history_prefix"
          :history_type="HISTORY_TYPES.SESSION"
        />
      </template>
    </template>
    <DialogModal
      v-model="sessionCloseDialog"
      :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
      :message="$vuetify.lang.t('$vuetify.gpt_sessions.session_close_alert_message')"
      :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
      :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
      @onConfirmation="closeSession()"
      @onCancel="sessionCloseDialog = false"
    />
    <DialogModal
      v-model="sessionBlockDialog"
      :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
      :message="$vuetify.lang.t('$vuetify.gpt_sessions.session_block_alert_message')"
      :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
      :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
      @onConfirmation="closeSession((isBlock = true))"
      @onCancel="sessionBlockDialog = false"
    />
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { encryptData, authorize } from "@/helpers/helper";
import { Role } from "@/helpers/role";
import { ACTION_BUTTON_GROUP } from "@/components/common/buttongroup/_buttongrouptypes";
import ButtonGroup from "@/components/common/buttongroup/ButtonGroup";
import getEnv from "@/config/env";
import { debounce } from "lodash";
import { HISTORY_TYPES } from "@/global/_constants";
import FileGenerationModal from "@/components/common/FileGenerationModal";
import FileGeneratedHistoryModal from "@/components/common/FileGeneratedHistoryModal";
import DialogModal from "@/components/common/DialogModal";
import TruncateTooltip from "@/components/common/tooltip/TruncateTooltip";

import {
  ADD_ALERT,
  GET_GPT_SESSIONS,
  GET_GPT_PROFILES,
  GENERATE_SESSION_HISTORY,
  UPDATE_GPT_SESSION,
  GET_SESSION_COUNT_PER_PROFILE,
} from "@/store/_actiontypes";

export default {
  props: {
    startDate: {
      type: String,
      required: false,
    },
    endDate: {
      type: String,
      required: false,
    },
  },
  components: {
    ButtonGroup,
    DialogModal,
    FileGenerationModal,
    FileGeneratedHistoryModal,
    TruncateTooltip,
  },
  data() {
    return {
      ACTION_BUTTON_GROUP: ACTION_BUTTON_GROUP,
      HISTORY_TYPES: HISTORY_TYPES,
      gptSessionFileGenerationModal: false,
      gptSessionFileHistoryModal: false,
      start_date: this.startDate,
      end_date: this.endDate,
      isLoading: false,
      isProfileListLoading: false,
      sessionList: [],
      searchBySessionId: "",
      searchByCode: "",
      page: 1,
      itemsPerPage: 5,
      totalHits: 0,
      returnedHits: 0,
      activeCount: 0,
      sessionListHeaders: [],
      gptSessionOptions: {
        itemsPerPage: 5,
        sortBy: [],
        sortDesc: [],
      },
      sessionDetailsModal: false,
      sessionDetails: {},
      playground_url: "",
      selectedProfileId: null,
      profilePage: 0,
      profilePageSize: 100,
      gptProfiles: [],
      hasMoreProfiles: false,
      session_history_prefix: "session_history_download",
      sessionSearchText: "",
      archiveFilter: null,
      archiveOptions: [
        { text: this.$vuetify.lang.t("$vuetify.gpt_history.active"), value: false },
        { text: this.$vuetify.lang.t("$vuetify.gpt_history.archived"), value: true },
      ],
      expandedItems: {},
      loadingItems: {},
      sessionBlockDialog: false,
      sessionCloseDialog: false,
      sessionToBeBlockedOrClosed: null,
      expandedRows: [],
    };
  },
  computed: {
    ...mapState({
      user: state => state.account.user,
      selectedProject: state => state.project.selectedProject,
    }),
    ...mapGetters("loader", ["loading"]),
    chevronIcon() {
      return itemId => (this.expandedItems[itemId] ? "mdi-chevron-up" : "mdi-chevron-down");
    },
  },
  watch: {
    startDate(newVal) {
      this.start_date = newVal;
      this.page = 1;
      this.debouncedGetGptSessions();
    },
    endDate(newVal) {
      this.end_date = newVal;
      this.page = 1;
      this.debouncedGetGptSessions();
    },
    itemsPerPage(newVal) {
      this.page = 1;
      this.debouncedGetGptSessions();
    },
    page(newVal) {
      this.debouncedGetGptSessions();
    },
    searchBySessionId(_newVal) {
      this.page = 1;
      this.debouncedGetGptSessions();
    },
    searchByCode(newVal) {
      this.page = 1;
      this.debouncedGetGptSessions();
    },
    sessionSearchText(newVal) {
      this.page = 1;
      this.debouncedGetGptSessions();
    },
    archiveFilter() {
      this.page = 1;
      this.debouncedGetGptSessions();
    },
    gptSessionOptions: {
      handler() {
        this.debouncedGetGptSessions();
      },
      deep: true,
    },
  },
  mounted() {
    this.page = 1;
    this.itemsPerPage = 5;
    this.debouncedGetGptSessions();
    this.getGPTProfiles();
  },
  created() {
    this.playground_url = getEnv("VUE_APP_MAIRA_APP_URL");
    this.sessionListHeaders = [
      {
        text: this.$vuetify.lang.t("$vuetify.gpt_sessions.session_id"),
        align: "start",
        sortable: false,
        value: "session_id",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.gpt_sessions.session_title"),
        align: "start",
        sortable: false,
        value: "session_title",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.gpt_sessions.session_code"),
        align: "start",
        sortable: false,
        value: "session_code",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.gpt_sessions.session_user_name"),
        align: "start",
        sortable: false,
        value: "user_name",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.gpt_sessions.profile_name"),
        align: "start",
        sortable: false,
        value: "maira_profile_name",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.common.ip_address"),
        align: "start",
        sortable: false,
        value: "ip_address",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.gpt_sessions.session_creation_date"),
        align: "start",
        sortable: true,
        value: "created_at",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.gpt_sessions.last_conversation_at"),
        align: "start",
        sortable: true,
        value: "last_conversation_at",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.gpt_sessions.session_status"),
        align: "start",
        sortable: true,
        value: "session_status",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.gpt_sessions.session_ended_at"),
        align: "start",
        sortable: true,
        value: "session_ended_at",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.gpt_sessions.session_closure_reason"),
        align: "start",
        sortable: false,
        value: "session_closure_reason",
      },
      {
        text: this.$vuetify.lang.t("$vuetify.table_header.actions"),
        align: "center",
        sortable: false,
        value: "actions",
      },
    ];
    this.debouncedGetGptSessions = debounce(this.getGptSessions, 500);
  },
  methods: {
    ...mapActions("session_list", [GET_GPT_SESSIONS, UPDATE_GPT_SESSION, GET_SESSION_COUNT_PER_PROFILE]),
    ...mapActions("project", [GET_GPT_PROFILES, GENERATE_SESSION_HISTORY]),
    resetExpandedProfilesOnClose() {
      this.expandedItems = {};
    },
    truncate(value, maxLength = 30, fallback = "N/A") {
      const text = String(value ?? fallback);

      return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
    },
    getGptSessions() {
      let { sortBy = [], sortDesc = [] } = this.gptSessionOptions;
      let params = {
        start: this.itemsPerPage != -1 ? (this.page - 1) * this.itemsPerPage : 0,
        size: this.itemsPerPage != -1 ? this.itemsPerPage : 10000,
      };

      if (sortBy?.length) {
        const sortArray = sortBy.map((field, index) => ({
          field: field || "created_at",
          order: sortDesc[index] ? "desc" : "asc",
        }));
        params.sort_by = JSON.stringify(sortArray);
      } else {
        params.sort_by = JSON.stringify([{ field: "created_at", order: "desc" }]);
      }
      if (this.sessionSearchText) {
        params.search_text = this.sessionSearchText.trim();
      }
      if (this.searchBySessionId) {
        params.session_id = this.searchBySessionId.trim();
      }
      if (this.selectedProfileId) {
        params.maira_profile_id = this.selectedProfileId;
      }
      if (this.searchByCode) {
        params.session_code = this.searchByCode.trim();
      }
      if (this.archiveFilter !== null && this.archiveFilter !== undefined) {
        params.is_archived = this.archiveFilter;
      }
      if (this.start_date) {
        params.start_date = this.start_date;
      }
      if (this.end_date) {
        params.end_date = this.end_date;
      }
      this.isLoading = true;
      this.GET_GPT_SESSIONS({
        project_id: this.selectedProject.id,
        params,
      })
        .then(
          response => {
            this.sessionList = response.data?.detail?.response?.sessions || [];
            this.totalHits = response.data?.detail?.response?.total_hits || 0;
            this.returnedHits = response.data?.detail?.response?.returned_hits || 0;
            this.activeCount = response.data?.detail?.response?.active_count || 0;
          },
          error => {
            console.error("error: ", error);
          }
        )
        .finally(() => {
          this.isLoading = false;
        });
    },
    closeSessionDetailsModal() {
      this.sessionDetailsModal = false;
    },
    openSessionDetailsModal(item) {
      this.sessionDetails = item;
      this.sessionDetailsModal = true;
    },
    formatDate(datestr) {
      const date = new Date(datestr);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    },
    redirectSessionConversationDetails(item) {
      const session_id = item.session_id;
      const data = {
        project_id: this.selectedProject.id,
        auth_token: this.user.access_token,
        refresh_token: this.user.refresh_token,
      };

      const parts = session_id.split("-");
      const secretKey = `${parts[0]}#${parts[parts.length - 1]}`;
      const urlSafeEncryptedData = encryptData(data, secretKey);
      const newTabUrl = `${getEnv("VUE_APP_MAIRA_APP_URL")}/session/${session_id}?token=${urlSafeEncryptedData}`;
      window.open(newTabUrl, "_blank");
    },
    getGPTProfiles() {
      this.isProfileListLoading = true;

      this.GET_GPT_PROFILES({
        project_id: this.selectedProject.id,
        start: this.profilePage * this.profilePageSize,
        size: this.profilePageSize,
      })
        .then(res => {
          const newProfiles = res?.data?.detail?.response.profiles || [];
          const totalHits = res?.data?.detail?.response?.total_hits || 0;
          this.gptProfiles = this.profilePage === 0 ? newProfiles : [...this.gptProfiles, ...newProfiles];
          this.hasMoreProfiles = this.gptProfiles.length < totalHits;
          this.profilePage++;
        })
        .catch(err => {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t("$vuetify.gpt_profile.profile_fetch_error"),
              color: "error",
            },
            { root: true }
          );
        })
        .finally(() => {
          this.isProfileListLoading = false;
        });
    },
    handleProfileChange(profile) {
      if (typeof profile === "string") {
        // ignoring search text input
        return;
      }
      this.page = 1;
      this.selectedProfileId = profile?.id;
      this.debouncedGetGptSessions();
    },
    sessionLogDownloadRequest({
      project_id,
      start_date,
      end_date,
      language,
      profile_id,
      session_id,
      session_code,
      search_text,
      is_archived,
    }) {
      return this.GENERATE_SESSION_HISTORY({
        project_id,
        start_date,
        end_date,
        language,
        profile_id,
        session_id,
        session_code,
        search_text,
        is_archived,
      });
    },
    closeSession(isBlock = false) {
      const params = {
        session_status: isBlock ? "blocked" : "finished",
      };
      this.UPDATE_GPT_SESSION({
        project_id: this.selectedProject.id,
        session_id: this.sessionToBeBlockedOrClosed.session_id,
        params,
      })
        .then(() => {
          this.sessionToBeBlockedOrClosed.session_status = isBlock ? "blocked" : "finished";
          this.$emit("session-status-updated", {
            session_id: this.sessionToBeBlockedOrClosed.session_id,
            session_status: this.sessionToBeBlockedOrClosed.session_status,
          });
          setTimeout(() => {
            this.debouncedGetGptSessions();
          }, 500);
          this.sessionToBeBlockedOrClosed.session_closure_reason = "manual";
        })
        .catch(err => {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t(
                isBlock ? "$vuetify.gpt_sessions.session_block_error" : "$vuetify.gpt_sessions.session_close_error"
              ),
              color: "error",
            },
            { root: true }
          );
        })
        .finally(() => {
          if (isBlock) this.sessionBlockDialog = false;
          else this.sessionCloseDialog = false;
        });
    },
    getSessionCountByProfile(profile) {
      if (this.expandedItems[profile.id]) {
        this.$set(this.expandedItems, profile.id, false);
        return;
      }
      this.$set(this.loadingItems, profile.id, true);
      this.$set(this.expandedItems, profile.id, true);
      this.GET_SESSION_COUNT_PER_PROFILE({
        project_id: this.selectedProject.id,
        maira_profile_id: profile.id,
        start_date: this.start_date,
        end_date: this.end_date,
      })
        .then(res => {
          this.$set(profile, "total_sessions", res.data?.detail?.response?.total_sessions ?? "N/A");
          this.$set(profile, "total_active_sessions", res.data?.detail?.response?.total_active_sessions ?? "N/A");
          this.$set(profile, "total_finished_sessions", res.data?.detail?.response?.total_finished_sessions ?? "N/A");
          this.$set(profile, "total_blocked_sessions", res.data?.detail?.response?.total_blocked_sessions ?? "N/A");
        })
        .catch(err => {
          this.$set(profile, "total_sessions", "N/A");
          this.$set(profile, "total_active_sessions", "N/A");
          this.$set(profile, "total_finished_sessions", "N/A");
          this.$set(profile, "total_blocked_sessions", "N/A");
        })
        .finally(() => {
          this.$set(this.loadingItems, profile.id, false);
        });
    },
    getIpAddresses(item) {
      const ips = item?.ip_address;

      if (!ips) return [];

      if (Array.isArray(ips)) {
        return ips.filter(ip => ip && String(ip).trim());
      }

      if (typeof ips === "string") {
        const ip = ips.trim();
        return ip ? [ip] : [];
      }

      return [];
    },
    toggleRowExpansion(item) {
      if (this.expandedRows.includes(item)) {
        this.expandedRows = this.expandedRows.filter(row => row !== item);
      } else {
        this.expandedRows.push(item);
      }
    },
  },
};
</script>
<style>
.sesstion-details-card-title {
  height: auto !important;
  min-height: unset !important;
}
.small-text {
  font-size: 0.75rem;
}
.custom-zindex {
  z-index: 3 !important;
}
.ip-list {
  word-spacing: 10px;
}
.clickable-more {
  cursor: pointer;
  color: var(--v-primary-base) !important;
  font-weight: 500;
  transition: color 0.2s ease;
}
.clickable-more:hover {
  text-decoration: underline;
  opacity: 0.8;
}
@media (max-width: 959px) {
  ::v-deep .v-btn__content .v-icon--left {
    margin-left: 5px !important;
    margin-right: 5px !important;
  }
}
</style>
